import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiCluAgentService } from 'src/app/services/api-clu-agent.service';
import { DEFAULT_DISPLAY_DEVICE_MESSAGES } from 'src/app/app.models';
import * as signalR from '@microsoft/signalr';
import { compareVersions } from 'compare-versions';
import { BehaviorSubject, catchError, lastValueFrom, Subscription } from 'rxjs';
import { GetNetworkErrorMessage, HandleError } from '../network-error-handler';
import { StartupInfos_App_CluAgent, DbmCluAgentParams } from './clu-agent.models';
import { CashdeskDevicesPrinterService } from 'src/app/services/cashdesk-devices-printer.service';
import { MatDialog } from '@angular/material/dialog';
import { MdCluAgentSettingsDevicesDialogComponent } from './@modals/md-clu-agent-settings-devices-dialog/md-clu-agent-settings-devices-dialog.component';
import { CashDeskCoreRxDbService } from 'src/app/@clu-fe/apps/m-cashdesk/@services/m-cashdesk-core-rxdb.service';
import { CluTickItem } from 'src/app/@clu-fe/apps/m-cashdesk/@models/clutick.model';
import { RxCluTickLigneDocumentType } from 'src/app/@clu-fe/apps/m-cashdesk/@schemas/clu_tickligne.schema';
import { FormatNumber } from '../format-number';
import { AuthService } from 'src/app/@auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class CluAgentService {
    connection: signalR.HubConnection;
    state$ = new BehaviorSubject<{ startupInfos: StartupInfos_App_CluAgent | null, cluParams: DbmCluAgentParams | null, error: string | null, loading: boolean }>({ startupInfos: null, cluParams: null, error: null, loading: false });
    cluAgentVersion = { APP_VERSION_ID: '0.0.0', APP_VERSION_DISPLAY: '' };
    
    private subscription: Subscription | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _apiCluAgentService: ApiCluAgentService,
        private _matDialog: MatDialog,
        public printerService: CashdeskDevicesPrinterService,
        private _cashDeskRxDbService : CashDeskCoreRxDbService,
        private _authService: AuthService
    ) {
    }

    async initConnection() {
        await this.doCheckCluAgentVersion();
        try {
            this.state$.next({ startupInfos: null, cluParams: null, error: null, loading: true });
            await this.doGetStartupInfosAction();
            await this.doGetParamsAction();
            await this.doConnectHub();
            this.updateStateValue({ loading: false });

            const state = this.state$.value;
            if (!!state.startupInfos) {
                if (state.startupInfos.DEVICE_DISPLAY_CONNECTED == true) { console.log("Afficheur connecté"); }
                else { console.log("Afficheur non connecté"); }
            }
        } catch (e) { }
    }

    disconnectCluAgent() {
        this.disconnectHub();
        this.state$.next({ startupInfos: null, cluParams: null, error: null, loading: false });
    }

    doGetListTicketsItemsAction_CashDeskSync(queryParams: any) {
        let httpParams = new HttpParams();
        Object.keys(queryParams).forEach(k => {
            httpParams = httpParams.set(k, queryParams[k]);
        });
        return lastValueFrom(this._httpClient.get<any>(this._apiCluAgentService.endPoints!.doGetListTicketsItemsAction_CashDeskSync, { params: httpParams })
                .pipe(catchError(error => HandleError(error, "Impossible de récupérer les tickets de l'Agent. Veuillez vérifier que l'Agent est en cours d'execution"))));
    }

    async doCheckCluAgentVersion() {
        try {
            this.cluAgentVersion = await lastValueFrom(this._httpClient.get<{ APP_VERSION_ID: string, APP_VERSION_DISPLAY: string }>(this._apiCluAgentService.endPoints!.doGetAppVersionAction_App));
            if (compareVersions(this.cluAgentVersion.APP_VERSION_ID, '2.0.0') < 0) {
                throw `La version de l'Agent Cauris Linkup connecté est obsolète ${this.cluAgentVersion.APP_VERSION_ID}. Veuillez mettre à jour votre application. Version supportée >= 2.0.0`;
            }
        } catch (err) { }
    }

    async doGetStartupInfosAction() {
        try {
            if (!this._apiCluAgentService.endPoints) { throw "Configuration Agent invalide"; }
            const startupInfos = await lastValueFrom(this._httpClient.get<StartupInfos_App_CluAgent>(this._apiCluAgentService.endPoints!.doGetStartupInfosAction_App));
            this.updateStateValue({ startupInfos: startupInfos, loading: false });
        } catch (error) {
            const errMessage = await GetNetworkErrorMessage(error, 'Impossible de se connecter à l\'AGENT CAURIS LINKUP: Vérifier la configuration');
            this.updateStateValue({ error: errMessage, loading: false });
        }
    }

    async doGetParamsAction() {
        try {
            if (!this._apiCluAgentService.endPoints) { throw "Configuration Agent invalide"; }
            const cluParams = await lastValueFrom(this._httpClient.get<DbmCluAgentParams>(this._apiCluAgentService.endPoints!.doGetParamsAction_App));
            this.updateStateValue({ cluParams: cluParams, loading: false });
        } catch (error) {
            const errMessage = await GetNetworkErrorMessage(error, 'Impossible de se connecter à l\'AGENT CAURIS LINKUP: Vérifier la configuration');
            this.updateStateValue({ error: errMessage, loading: false });
        }
    }

    updateStateValue(obj: { startupInfos?: StartupInfos_App_CluAgent | null, cluParams?: DbmCluAgentParams | null, error?: string | null, loading?: boolean }) {
        let currentStateValue = this.state$.value;
        currentStateValue = Object.assign(currentStateValue, obj);
        this.state$.next(currentStateValue);
    }

    getApiEndpointCluAgent() {
        return localStorage.getItem('CAURIS_LINKUP_FOP_AGENT_URL') || null;
    }

    setApiEndpointCluAgent(_apiendpoint: string) {
        this._apiCluAgentService.setEndpointUrl(_apiendpoint);
    }

    doSaveParamsAction(params: any) {
        return new Promise<void>(async (resolve, reject) => {
            if (!this._apiCluAgentService.endPoints) { reject("Configuration Agent invalide"); }
            try {
                await lastValueFrom(this._httpClient.post<void>(this._apiCluAgentService.endPoints!.doSaveParamsAction_App, params));
                this.updateStateValue({ cluParams: params });
                resolve();
            } catch (error) {
                reject(GetNetworkErrorMessage(error));
            }
        });
    }

    doOpenSettingsDevicesDialog() {
        return lastValueFrom(this._matDialog.open(MdCluAgentSettingsDevicesDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '100%',
            maxWidth: '600px',
            data: {
            }
        }).afterClosed());
    }

    // HUB CONNECTION ******************************************************
    private doConnectHub(): Promise<void> {
        
        const buildedUrl = `${this._apiCluAgentService.baseEndpointUrl}/cluagenthub`;
        this.connection = new signalR.HubConnectionBuilder()
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.None)
            .withUrl(buildedUrl)
            .build();

        this.connection.onreconnected((connectionId) => {
            this.refreshState();
        });

        this.connection.onclose(() => {
            this.state$.next({ startupInfos: null, cluParams: null, error: null, loading: false });
        });

        this.connection.on('DISPLAY_DEVICE_CONNECTED', () => {
            const state = this.state$.value;
            if (!!state.startupInfos) {
                state.startupInfos.DEVICE_DISPLAY_CONNECTED = true;
                this.updateStateValue(state);
            }
            console.log("Afficheur connecté");
        });
        this.connection.on('DISPLAY_DEVICE_DISCONNECTED', () => {
            const state = this.state$.value;
            if (!!state.startupInfos) {
                state.startupInfos.DEVICE_DISPLAY_CONNECTED = false;
                this.updateStateValue(state);
            }
            console.log("Afficheur déconnecté");
        });

        return this.connection.start();
    }

    isCluAgentConnected(): boolean {
        return !!this.connection && this.connection.state == signalR.HubConnectionState.Connected;
    }

    refreshState() {
        this.updateStateValue(this.state$.value);
    } 

    private disconnectHub() {
        if (!!this.connection && this.connection.state == signalR.HubConnectionState.Connected) {
            this.connection.stop();
        }
    }
    //



    // DISPLAY *****************************************************
    async doShowParametizedTextDisplayAction(codeParams: string, datas: { ticket?: CluTickItem, ligne?: RxCluTickLigneDocumentType, reglement?: any }) {
        try {
            if (!this._apiCluAgentService.endPoints) { throw "Configuration Agent invalide"; }
            if (!this.isCluAgentConnected()) { return; }

            const state = this.state$.value;
            if (!state.startupInfos?.DEVICE_DISPLAY_CONNECTED) { return; }

            let displayMessage: any = null;
            try {
                const pParams = await this._cashDeskRxDbService.db.pparams.findOne({ 
                    selector: { "CBMARQ": { "$eq": "1" } }, 
                    sort: [] 
                }).exec();
                if (!pParams) { throw "Impossible de récupérer les informations des paramètres"; }

                let displayDeviceMessages = null;
                try { displayDeviceMessages = JSON.parse(pParams.DISPLAY_DEVICE_MESSAGES!); } catch(err) {}
                if (displayDeviceMessages == null || !displayDeviceMessages[codeParams]) { throw "Les paramètres de l'afficheur sont introuvables"; }
                displayMessage = JSON.parse(JSON.stringify(displayDeviceMessages[codeParams]));
            } catch(err) {}

            if (displayMessage == null) {
                const index = DEFAULT_DISPLAY_DEVICE_MESSAGES.findIndex(i => i.ID == codeParams);
                if (index == -1) { throw "Les paramètres par défaut de l'afficheur sont introuvables"; }
                displayMessage = JSON.parse(JSON.stringify(DEFAULT_DISPLAY_DEVICE_MESSAGES[index]));
            }

            if (!!datas) {
                displayMessage.TEXT1 = this.formatTextDisplay(displayMessage.TEXT1, datas);
                displayMessage.TEXT2 = this.formatTextDisplay(displayMessage.TEXT2, datas);
            }

            if (this.subscription != null) { this.subscription.unsubscribe(); }
            this.subscription = this._httpClient.post<void>(this._apiCluAgentService.endPoints!.doShowTextAction_SerialDisplay, { TEXT_1: displayMessage.TEXT1, TEXT_2: displayMessage.TEXT2 }).subscribe(() => {
                this.subscription = null;
            });
        } catch(err) {
            console.error(err);
        }
    }

    formatTextDisplay(text: string, datas: any) {
        let formatedText = text;
        if (!!datas.ticket) {
            formatedText = formatedText
                .replaceAll("@[DO_NB_LIGNES]", (datas.ticket.ENTETE.DO_NB_LIGNES ?? 0).toString())
                .replaceAll("@[RG_ESP_RENDU]", FormatNumber(datas.ticket.ENTETE.DO_RG_ESP_RENDU_TOT ?? 0))
                .replaceAll("@[DO_MONTANTREGLE]", FormatNumber(datas.ticket.ENTETE.DO_MONTANTREGLE ?? 0))
                .replaceAll("@[DO_TOTALTTC]", FormatNumber(datas.ticket.ENTETE.DO_TOTALTTC))
                .replaceAll("@[DO_SOLDETTC]", FormatNumber(datas.ticket.ENTETE.DO_SOLDETTC));
        }

        if (!!datas.ligne) {
            formatedText = formatedText
                .replaceAll("@[DL_DESIGN]", datas.ligne.DL_DESIGN)
                .replaceAll("@[DL_QTE]", datas.ligne.DL_QTE)
                .replaceAll("@[DL_PUTTC]", FormatNumber(datas.ligne.DL_PUTTC ?? 0))
                .replaceAll("@[DL_MONTANTTTC]", FormatNumber(datas.ligne.DL_MONTANTTTC));
        }

        if (!!datas.reglement?.N_REGLEMENTLIB) { formatedText = formatedText.replaceAll("@[N_REGLEMENTLIB]", datas.reglement.N_REGLEMENTLIB); }
        if (!!datas.reglement?.RG_MONTANT) { formatedText = formatedText.replaceAll("@[RG_MONTANT]", FormatNumber(datas.reglement.RG_MONTANT)); }

        return formatedText;
    }

    doShowTextDisplayAction(text1: string, text2: string) {
        try {
            if (!this._apiCluAgentService.endPoints) { throw "Configuration Agent invalide"; }
            if (!this.isCluAgentConnected()) { return; }
            if (this.subscription != null) {
                this.subscription.unsubscribe();
            }
            this.subscription = this._httpClient.post<void>(this._apiCluAgentService.endPoints!.doShowTextAction_SerialDisplay, { TEXT_1: text1, TEXT_2: text2 }).subscribe(() => {
                this.subscription = null;
            });
        } catch(err) {
            console.error(err);
        }
    }
    
    doTestDisplayAction(displayId: string) {
        return new Promise<void>(async (resolve, reject) => {
            if (!this._apiCluAgentService.endPoints) { reject("Configuration Agent invalide"); }
            try {
                await lastValueFrom(this._httpClient.post<void>(this._apiCluAgentService.endPoints!.doTestDisplayAction_SerialDisplay, { DISPLAY_ID: displayId }));
                resolve();
            } catch (error) {
                reject(GetNetworkErrorMessage(error));
            }
        });
    }
    // ************************************************************

    // CASH BALANCE ***********************************************
    doGetCurrentWeightInfosAction() {
        return new Promise<any>(async (resolve, reject) => {
            if (!this._apiCluAgentService.endPoints) { reject("Configuration Agent invalide"); }
            try {
                const weightInfos = await lastValueFrom(this._httpClient.get<any>(this._apiCluAgentService.endPoints!.doGetCurrentWeightInfosAction_SerialCashBalance));
                resolve(weightInfos);
            } catch (error) {
                reject(GetNetworkErrorMessage(error));
            }
        });
    }
    // ************************************************************

    doSaveStandardPrinterConfigAction(stdPrinterConfig: any) {
        return lastValueFrom(this._httpClient.post<void>(this._apiCluAgentService.endPoints!.doSaveStandardPrinterConfigAction_StandardPrinter, stdPrinterConfig).pipe(catchError(error => HandleError(error))));
    }
}